import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { SupportRequestSlackPayload } from '@app/modules/admin/interfaces/support-request.interface'
import { SupportRequestTypeLabels } from '@app/modules/admin/services/support-request.service'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { PosthogService } from '@app/services/posthog.service'
import { TransformedV3User } from '@app/v2/shared/transformers/user-transformer.service'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { Observable, of } from 'rxjs'
import TurndownService from 'turndown'

/**
 * Interface for workspace notification data
 */
export interface WorkspaceNotificationData {
  workspace: Workspace
  user: TransformedV3User
  countryName: string
  secondsLookBack?: number
}

/**
 * Handles integrations between MICEtribe and third party suppliers
 */
@Injectable({
  providedIn: 'root'
})
export class IntegrationService {
  /**
   * Creates the service and injects it's dependencies
   * @param http HttpClient
   */
  constructor(
    private http: HttpClient,
    private _posthogService: PosthogService,
    private _authService: AuthService
  ) {}

  /**
   * Sends a message to Slack
   * @param payload string - Payload to be sent to Slack
   * @param enableInternalEmailChecks boolean - Whether to check if the current user is a production internal email
   * @returns Observable
   */
  sendSlackMessage(payload: any, enableInternalEmailChecks: boolean = true): Observable<any> {
    if (this._isProductionInternalEmail() && enableInternalEmailChecks) return of(null)
    return this.http.post(`${environment.planningApi}slack`, payload)
  }

  /**
   * Checks if the current user is a production internal email
   * @returns boolean
   */
  private _isProductionInternalEmail(): boolean {
    const ownerEmail = this._authService.currentWorkspace?.workspace_users?.find((user) => user.is_owner)?.user?.email ?? this._authService.userEmail;
    const isInternalEmail = !!ownerEmail && (ownerEmail.endsWith('@micetribe.com') || ownerEmail.endsWith('@contactless.io') || ownerEmail === 'oda.test.qa@gmail.com')
    return isInternalEmail && environment.production
  }

  /**
   * Sends a workspace registration notification to Slack
   * @param data WorkspaceNotificationData - Data containing workspace, user, and country information
   * @returns Observable
   */
  sendWorkspaceRegistrationNotification(data: WorkspaceNotificationData): Observable<any> {
    const { workspace, user, countryName } = data
    const sessionReplayURL = this._posthogService.getSessionReplayURL(data.secondsLookBack || 20)

    const message = [
      {
        type: 'divider'
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '*A new workspace has been registered with the following details:*'
        }
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '*Workspace Name:*\n' + workspace.name
        }
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '*Workspace Slug:*\n' + workspace.slug
        }
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '*Owner:*\n' + user?.full_name
        }
      },
      {
        type: 'section',
        fields: [
          {
            type: 'mrkdwn',
            text: '*Email:*\n' + user?.email
          },
          {
            type: 'mrkdwn',
            text: '*Time:*\n' + new Date().toISOString()
          }
        ]
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '*Phone:*\n' + user?.phone_number
        }
      },
      {
        type: 'context',
        elements: [
          {
            type: 'image',
            image_url: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // clean pin icon
            alt_text: 'Location'
          },
          {
            type: 'mrkdwn',
            text: '*Country:*\n' + countryName
          }
        ]
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '*Session Recording:*\n<' + sessionReplayURL + '|View Replay (Public)>'
        }
      },
      {
        type: 'divider'
      }
    ]

    return this.sendSlackMessage({
      blocks: message
    })
  }

  /**
   * Sends a support request notification to Slack
   * @param payload SupportRequestSlackPayload - Data containing support request details
   * @returns Observable
   */
  sendSupportRequest(payload: SupportRequestSlackPayload): Observable<any> {
    const { eventName, eventSlug, workspaceName, workspaceSlug, requestType, personas, personaNames, fields, description, assignedTo, createdBy } = payload

    // Convert HTML to Slack markdown using Turndown
    const turndownService = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
      bulletListMarker: '*' // Use * for bullet lists
    })

    // Add custom rules for Slack markdown format
    turndownService.addRule('slackBold', {
      filter: ['strong', 'b'],
      replacement: (content) => `*${content}*`
    })

    turndownService.addRule('slackItalic', {
      filter: ['em', 'i'],
      replacement: (content) => `_${content}_`
    })

    turndownService.addRule('slackStrikethrough', {
      filter: ['s', 'del'],
      replacement: (content) => `~${content}~`
    })

    turndownService.addRule('slackCode', {
      filter: 'code',
      replacement: (content) => `\`${content}\``
    })

    // Convert bullet markers from * to • after conversion
    let descriptionMarkdown = turndownService.turndown(description)
    descriptionMarkdown = descriptionMarkdown.replace(/^\* /gm, '• ')

    // Slack text blocks have a 1800 character limit, so truncate if necessary
    const SLACK_TEXT_BLOCK_LIMIT = 1800
    const descriptionPreview = descriptionMarkdown.length > SLACK_TEXT_BLOCK_LIMIT ? descriptionMarkdown.substring(0, SLACK_TEXT_BLOCK_LIMIT) + '...' : descriptionMarkdown

    // Build persona and field info
    let personaFieldInfo = ''

    // Show each selected persona with their fields
    if (personaNames && personaNames.length > 0) {
      personaFieldInfo = '*Selected Personas & Fields:*\n'

      personaNames.forEach((personaName) => {
        // Find fields for this persona
        const personaFields = fields?.find((f) => f.personaName === personaName)

        if (personaFields && personaFields.fields && personaFields.fields.length > 0) {
          // Specific fields selected - show as "Label (name)"
          const formattedFields = personaFields.fields.map((field) => `${field.label} (${field.name})`).join(', ')
          personaFieldInfo += `  • *${personaName}:* ${formattedFields}\n`
        } else if (personaFields && personaFields.fieldNames.length > 0) {
          // Fallback to field names only (for backward compatibility)
          personaFieldInfo += `  • *${personaName}:* ${personaFields.fieldNames.join(', ')}\n`
        } else {
          // No specific fields selected for this persona
          personaFieldInfo += `  • *${personaName}:* _All fields_\n`
        }
      })

      personaFieldInfo += '\n'
    }

    const message = [
      {
        type: 'divider'
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: '🆘 *New Support Request*'
        }
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*Event:* ${eventName}\n*Event Slug:* \`${eventSlug}\``
        }
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*Workspace:* ${workspaceName}\n*Workspace Slug:* \`${workspaceSlug}\``
        }
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*Request Type:* ${SupportRequestTypeLabels[requestType]}`
        }
      }
    ]

    // Add persona and field info if available
    if (personaFieldInfo) {
      message.push({
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: personaFieldInfo
        }
      })
    }

    // Add description
    message.push({
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `*Description:*\n${descriptionPreview}`
      }
    })

    // Add requester info
    message.push({
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `*Requested by:* ${createdBy.name} (${createdBy.email})`
      }
    })

    // Add assigned person with mention
    message.push({
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `*Assigned to:* <@${assignedTo.filter((id) => id).join('>, <@')}>`
      }
    })

    message.push({
      type: 'divider'
    })

    const slackMessagePayload = { blocks: message }
    if (environment.production) slackMessagePayload['channel'] = 'support'
    return this.sendSlackMessage(slackMessagePayload, false)
  }
}
